import type { ReactNode } from 'react';
import { Tooltip } from '@components/ui/Tooltip/Tooltip';
import styles from './styles.module.css';

/** How far off the figure is: `good`/`neutral` stay calm, `warn`/`bad` take colour. */
export type MetricTone = 'good' | 'neutral' | 'warn' | 'bad';

type IMetricBarProps = {
  label: string;
  /** The figure, bold — omitted when there is none to show. */
  value?: ReactNode;
  /** Muted text after the figure ("plan 40%", "17/25", "none due"). */
  context?: ReactNode;
  /** Colours the context alone, for a delta that reads good or bad on its own. */
  contextTone?: 'good' | 'bad';
  /** Between figure and context; a context that continues the figure
   *  ("80 / 75 expected") passes a plain space. */
  separator?: string;
  /** 0–100; `null` leaves the track empty (nothing to measure yet). */
  fillPct: number | null;
  tone: MetricTone;
  /** A target marker on the track (plan, expected), 0–100. */
  tickPct?: number;
  /** A shaded target range on the track, 0–100. */
  band?: { fromPct: number; toPct: number };
  /** Hover explanation of what the figure measures. */
  hint?: string;
  testId: string;
};

const clamp = (v: number) => Math.min(100, Math.max(0, v));

/**
 * One of the card's performance figures: a small label and figure over a thin
 * bar. Deliberately unlike the page's KPI tiles — big numbers belong to the
 * portfolio as a whole, a card shows its project as bars.
 */
export function MetricBar({ label, value, context, contextTone, separator = ' · ', fillPct, tone, tickPct, band, hint, testId }: IMetricBarProps) {
  const row = (
    <div className={styles.metric} data-testid={testId} data-tone={tone}>
      <div className={styles.head}>
        <span className={styles.label}>{label}</span>
        <span className={styles.figures}>
          {value === undefined ? null : <b className={styles.value}>{value}</b>}
          {value !== undefined && context !== undefined ? separator : null}
          {context === undefined ? null : (
            <span className={styles.context} data-tone={contextTone}>
              {context}
            </span>
          )}
        </span>
      </div>
      <div className={styles.track} aria-hidden>
        {band ? (
          <span className={styles.band} style={{ left: `${clamp(band.fromPct)}%`, width: `${clamp(band.toPct) - clamp(band.fromPct)}%` }} />
        ) : null}
        {fillPct === null ? null : <span className={styles.fill} data-testid={`${testId}-fill`} style={{ width: `${clamp(fillPct)}%` }} />}
        {tickPct === undefined ? null : <span className={styles.tick} data-testid={`${testId}-tick`} style={{ left: `${clamp(tickPct)}%` }} />}
      </div>
    </div>
  );
  return hint ? <Tooltip content={hint}>{row}</Tooltip> : row;
}
